import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LinkExternal01Icon } from '@untitledui-icons/react/line'
import { usePermissions } from '@/hooks/api'

// Configuration for which reservation systems have screenshot support
const SCREENSHOT_ENABLED_TOOLS = ['zenchef', 'formitable', 'fork', 'guestplan']

interface ToolCallScreenshotLinkProps {
  toolCallId: string
  toolName?: string
  conversationDate?: string
  assistantConfig?: unknown
}

/**
 * Component to display screenshot links for reservation tool calls.
 *
 * Shows a link to view availability screenshots for supported reservation systems.
 * Only visible to admin users when the assistant uses a supported system.
 */
export function ToolCallScreenshotLink({
  toolCallId,
  toolName,
  conversationDate,
  assistantConfig,
}: ToolCallScreenshotLinkProps) {
  const { t } = useTranslation()
  const { isAdmin } = usePermissions()

  // Only show for admin users and get_availability tool
  const isGetAvailabilityTool = toolName === 'get_availability'

  // Check if assistant uses a supported reservation system
  const hasScreenshotSupport = useMemo(() => {
    if (!assistantConfig) return false

    try {
      // Config might be a string or object
      const config =
        typeof assistantConfig === 'string'
          ? JSON.parse(assistantConfig)
          : assistantConfig

      // Check if tools array contains any screenshot-enabled tool
      if (config.tools && Array.isArray(config.tools)) {
        return config.tools.some(
          (tool: { name?: string }) =>
            tool.name && SCREENSHOT_ENABLED_TOOLS.includes(tool.name.toLowerCase())
        )
      }

      return false
    } catch (error) {
      console.error('Error checking screenshot support:', error)
      return false
    }
  }, [assistantConfig])

  // Don't render if conditions aren't met
  if (!isAdmin || !hasScreenshotSupport || !toolCallId || !isGetAvailabilityTool) {
    return null
  }

  // Generate screenshot URL - return null if it fails
  const getScreenshotUrl = (id: string): string | null => {
    try {
      // Use conversation date if available, otherwise fallback to today
      const date = conversationDate ? new Date(conversationDate) : new Date()
      const dateFolder = date.toISOString().slice(0, 10).replace(/-/g, '') // yyyyMMdd format
      return `https://availability-screenshots.ams3.digitaloceanspaces.com/${dateFolder}/${id}.png`
    } catch (error) {
      console.error('Error generating screenshot URL:', error)
      return null
    }
  }

  const screenshotUrl = getScreenshotUrl(toolCallId)

  // Don't render if URL generation failed
  if (!screenshotUrl) {
    return null
  }

  return (
    <div className="mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
      <a
        href={screenshotUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
      >
        {t('resources.conversations.screenshot.view', 'View Screenshot')}
        <LinkExternal01Icon className="h-3 w-3 opacity-60" />
      </a>
    </div>
  )
}

export default ToolCallScreenshotLink
